import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { normalizeShiftReportTemplate, type ShiftReportTemplate } from '@/utils/shiftReportTemplate';

export const useShiftReportTemplate = () => {
  const { organizationId } = useAuth();

  return useQuery({
    queryKey: ['shift-report-template', organizationId],
    enabled: !!organizationId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('organizations')
        .select('id, shift_report_config')
        .eq('id', organizationId)
        .maybeSingle();
      if (error) throw error;
      return normalizeShiftReportTemplate(data?.shift_report_config);
    },
  });
};

export const useUpdateShiftReportTemplate = () => {
  const qc = useQueryClient();
  const { organizationId, user } = useAuth();

  return useMutation({
    mutationFn: async (template: ShiftReportTemplate) => {
      let orgId = organizationId;

      if (!orgId && user?.id) {
        const { data, error } = await supabase.rpc('get_user_org_id', { _user_id: user.id });
        if (error) throw error;
        orgId = data;
      }

      if (!orgId) throw new Error('Your organization is still loading. Please wait a moment and try again.');

      const { error } = await (supabase as any)
        .from('organizations')
        .update({ shift_report_config: { sections: template } })
        .eq('id', orgId);
      if (error) throw error;
      return orgId;
    },
    onSuccess: (orgId) => {
      qc.invalidateQueries({ queryKey: ['shift-report-template', orgId] });
      qc.invalidateQueries({ queryKey: ['organization', orgId] });
    },
  });
};
